import React, { useState, useEffect, useMemo } from "react";
import getColumns from "./Columns";
import { getCrossPlatformQAReport } from "../../Flux/Actions/MasterCampaignActions";
import { v4 as uuid } from "uuid";
import { TABS, PLATFORM_MAP, ID_NAME_MAP } from "./constants";
import _ from "lodash";

export const useColumnsAndData = ({ campaignId, selectedPlatforms, tab, refreshFlag, showAllOSColumns, loadingData }) => {
	const [rawData, setRawData] = useState([]);
	const [packagesAndPlacementData, setPackagesAndPlacementData] = useState([]);
	const [campaignsData, setCampaignsData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);

	// requests the required data from the API, parses it, and sets it into state.
	useEffect(() => {
		try {
			setRawData([]);
			setPackagesAndPlacementData([]);
			setCampaignsData([]);
			setError(false);
			const platformIDs = selectedPlatforms.map((platform) => {
				return platform.value;
			});
			if (campaignId && platformIDs.length !== 0) {
				setLoading(true);
				loadingData(true);
				getCrossPlatformQAReport(campaignId, platformIDs)
					.then((res) => {
						setRawData(res);
						setLoading(false);
						setError(false);
						loadingData(false);
					})
					.catch(() => {
						setRawData([]);
						setLoading(false);
						setError(true);
						loadingData(false);
					});
			}
		} catch (e) {
			console.log("An error occured while parsing the response from crossplatformqareport request");
		}
	}, [campaignId, selectedPlatforms, refreshFlag]);

	// map the raw data from the network response to collated state data to be rendered by the ComparisonGrid
	useEffect(() => {
		const selectedPlatformIDs = selectedPlatforms.map((selectedPlatform) => {
			return selectedPlatform.value.toString();
		});
		const osData = rawData?.find(({ MasterPlatformID }) => MasterPlatformID == "OS");
		const externalPlatformDataArray = rawData?.filter((data) => {
			return selectedPlatformIDs.includes(data?.MasterPlatformID);
		});
		const collatedData = [];

		if (osData && externalPlatformDataArray?.length !== 0) {
			switch (tab) {
				case TABS.PACKAGES_AND_PLACEMENTS:
					osData.PackagePlacements.forEach((el, i) => {
						const collatedObject = { id: uuid() };
						Object.entries(el).forEach(([key, val]) => {
							collatedObject[`${PLATFORM_MAP.OS.prefix}${key}`] = val;
							externalPlatformDataArray.forEach((platformData) => {
								const platformEl = platformData?.PackagePlacements?.[i];
								collatedObject[`${PLATFORM_MAP?.[ID_NAME_MAP[platformData.MasterPlatformID]]?.prefix}${key}`] = platformEl?.[key] || null;
							});
						});
						collatedData.push(collatedObject);
					});
					setPackagesAndPlacementData(collatedData);
					break;
				case TABS.CAMPAIGNS:
					const el = osData?.Campaign;
					const collatedObject = { id: uuid() };
					Object.entries(el).forEach(([key, val]) => {
						collatedObject[`${PLATFORM_MAP.OS.prefix}${key}`] = val;
						selectedPlatformIDs.forEach((platformID) => {
							const platformEl = rawData?.find(({ MasterPlatformID }) => MasterPlatformID == platformID)?.Campaign;
							collatedObject[`${PLATFORM_MAP?.[ID_NAME_MAP[platformID]]?.prefix}${key}`] = platformEl?.[key] || null;
						});
					});
					collatedData.push(collatedObject);
					setCampaignsData(collatedData);
					break;
			}
		}
	}, [rawData, tab, selectedPlatforms]);

	// determine which columns to show based on errors present in the data
	const columns = useMemo(() => {
		let selectedPlatformKey = null;
		if (selectedPlatforms.length === 1) {
			selectedPlatformKey = ID_NAME_MAP[selectedPlatforms[0].value];
		} else if (selectedPlatforms.length > 1) {
			const selectedPlatformIDs = selectedPlatforms.map((selectedPlatform) => {
				return selectedPlatform.value.toString();
			});
			if (_.size(selectedPlatformIDs) === _.size(["1", "5"]) && _.difference(selectedPlatformIDs, ["1", "5"]).length === 0) {
				selectedPlatformKey = "GCMPRISMA";
			}
		}
		switch (tab) {
			case TABS.PACKAGES_AND_PLACEMENTS:
				return getColumns(packagesAndPlacementData, showAllOSColumns, selectedPlatformKey, "packagesPlacements");
			case TABS.CAMPAIGNS:
				return getColumns(campaignsData, showAllOSColumns, selectedPlatformKey, "campaign");
		}
	}, [showAllOSColumns, selectedPlatforms, packagesAndPlacementData, campaignsData]);

	// expose either placement or campaign data based on the tab
	const data = useMemo(() => {
		switch (tab) {
			case TABS.PACKAGES_AND_PLACEMENTS:
				return packagesAndPlacementData;
			case TABS.CAMPAIGNS:
				return campaignsData;
		}
	}, [tab, campaignsData, packagesAndPlacementData]);

	// expose the required memoized values to the consumer
	return useMemo(() => ({ columns, loading, error, data }), [loading, error, data, columns]);
};
